import { useState, useTransition } from "react";
import { useRevalidate } from "@/lib/loader";
import type { Role, DisplayStatus } from "@/lib/auth/access";
import { inviteUser, changeUserRole, setUserStatus, removeUser } from "./usersActions";

type Row = {
  email: string;
  role: Role;
  status: DisplayStatus;
  addedAt: string;
  isUser: boolean;
};
const FILTERS = ["All", "Pending", "Active", "Disabled"] as const;

export function UsersTable({
  rows,
  currentEmail,
  envSuperAdmins,
}: {
  rows: Row[];
  currentEmail: string;
  envSuperAdmins: string[];
}) {
  const revalidate = useRevalidate();
  const [pending, startTransition] = useTransition();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("MEMBER");
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("All");

  const envSet = new Set(envSuperAdmins.map((e) => e.toLowerCase()));
  const locked = (e: string) => e.toLowerCase() === currentEmail.toLowerCase() || envSet.has(e.toLowerCase());

  function run(action: () => Promise<{ ok: boolean; error?: string }>) {
    setError(null);
    startTransition(async () => {
      const res = await action();
      if (!res.ok) setError(res.error ?? "Action failed");
      else revalidate();
    });
  }

  const shown = rows.filter((r) => filter === "All" || r.status === filter);

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(async () => {
            const res = await inviteUser(email, role);
            if (res.ok) setEmail("");
            return res;
          });
        }}
        className="flex flex-wrap items-end gap-2 rounded border border-zinc-200 dark:border-border bg-white dark:bg-surface p-3"
      >
        <label className="flex flex-col text-xs text-zinc-600 dark:text-zinc-400">
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@company.com"
            className="mt-1 w-64 rounded border border-zinc-300 bg-white px-2 py-1 text-sm text-zinc-900 dark:border-border dark:bg-surface dark:text-zinc-100"
          />
        </label>
        <label className="flex flex-col text-xs text-zinc-600 dark:text-zinc-400">
          Role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="mt-1 rounded border border-zinc-300 bg-white px-2 py-1 text-sm text-zinc-900 dark:border-border dark:bg-surface dark:text-zinc-100"
          >
            <option value="MEMBER">Member</option>
            <option value="SUPER_ADMIN">Super admin</option>
          </select>
        </label>
        <button
          type="submit"
          disabled={pending}
          className="rounded bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
        >
          Add user
        </button>
        {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
      </form>

      <div className="flex gap-1">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={[
              "rounded px-2 py-1 text-xs font-medium",
              filter === f ? "bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 text-white" : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-surface-2",
            ].join(" ")}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded border border-zinc-200 dark:border-border bg-white dark:bg-surface">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-zinc-100 dark:bg-surface-2 text-xs uppercase text-zinc-600 dark:text-zinc-400">
            <tr>
              <th className="px-4 py-2 font-semibold">Email</th>
              <th className="px-4 py-2 font-semibold">Role</th>
              <th className="px-4 py-2 font-semibold">Status</th>
              <th className="px-4 py-2 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-500 dark:text-zinc-400">No users.</td></tr>
            )}
            {shown.map((r) => {
              const isLocked = locked(r.email);
              return (
                <tr key={r.email} className="border-t border-zinc-100 dark:border-border">
                  <td className="px-4 py-3 font-medium text-zinc-900 dark:text-zinc-100">{r.email}</td>
                  <td className="px-4 py-3">
                    <select
                      value={r.role}
                      disabled={isLocked || pending}
                      onChange={(e) => run(() => changeUserRole(r.email, e.target.value as Role))}
                      className="rounded border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-900 disabled:opacity-50 dark:border-border dark:bg-surface dark:text-zinc-100"
                    >
                      <option value="MEMBER">Member</option>
                      <option value="SUPER_ADMIN">Super admin</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">{r.status}</td>
                  <td className="px-4 py-3 text-right">
                    {!isLocked && (
                      <span className="inline-flex gap-2">
                        {r.status === "Active" && (
                          <button type="button" disabled={pending}
                            onClick={() => run(() => setUserStatus(r.email, "DISABLED"))}
                            className="text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline disabled:opacity-50">Disable</button>
                        )}
                        {r.status === "Disabled" && (
                          <button type="button" disabled={pending}
                            onClick={() => run(() => setUserStatus(r.email, "ACTIVE"))}
                            className="text-xs font-medium text-emerald-700 dark:text-emerald-300 hover:underline disabled:opacity-50">Enable</button>
                        )}
                        <button type="button" disabled={pending}
                          onClick={() => run(() => removeUser(r.email))}
                          className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline disabled:opacity-50">Remove</button>
                      </span>
                    )}
                    {isLocked && <span className="text-xs text-zinc-400 dark:text-zinc-500">Locked</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
